import {useState} from "react";
import {Button} from "@/components/ui/Button/index";
import {Calendar, Loader, Store, Sparkles, GitPullRequest, Eye} from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import Badge from "@/components/Badge";
import {Checkbox} from "@/components/ui/Checkbox/index";
import {apiCommentsReview} from '@/services/ShredService.ts';

interface PublishForReviewProps {
    shredId: string | undefined;
    onPublish: () => void;
    onPublishError: (error: string) => void;
}

const PublishForReview = (props: PublishForReviewProps) => {
    const {shredId, onPublish, onPublishError} = props;

    const [searching] = useState<boolean>(false);
    const [review] = useState<boolean>(false);
    const [compileAfterEnd, setCompileAfterEnd] = useState<boolean>(true);
    const [shredReviewLoading, setShredReviewLoading] = useState<boolean>(false);

    // Restored review function from original implementation
    const reviewFunc = async () => {
        try {
            if (!shredId) return;

            onPublishError("");
            setShredReviewLoading(true);

            const response = await apiCommentsReview(shredId, compileAfterEnd);
            if (response.status === 200) {
                onPublish();
            }
        } catch (e) {
            let errorMessage = "An error occurred while reviewing the pull request comments";

            // Handle axios/fetch error responses with status codes
            if (e && typeof e === 'object' && 'response' in e) {
                const axiosError = e as { response: { data: any } };
                if (axiosError.response && axiosError.response.data) {
                    // Extract message from API response
                    const responseData = axiosError.response.data;
                    if (responseData.message) {
                        errorMessage = responseData.message;
                    } else {
                        errorMessage = `${errorMessage}: ${JSON.stringify(responseData)}`;
                    }
                }
            } else if (e instanceof Error) {
                errorMessage = `${errorMessage}: ${e.message}`;
            } else if (typeof e === 'object' && e !== null) {
                try {
                    errorMessage = `${errorMessage}: ${JSON.stringify(e)}`;
                } catch {
                    // If JSON stringify fails, keep the default message
                }
            } else if (e !== null && e !== undefined) {
                errorMessage = `${errorMessage}: ${String(e)}`;
            }

            onPublishError(errorMessage);
        } finally {
            setShredReviewLoading(false);
        }
    }

    return (
        <div
            className="col-span-12 lg:col-span-6 bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 p-4 grid grid-cols-12 gap-6 rounded-3xl min-h-[280px] shadow-lg">
            {searching ? (
                <div className="col-span-12 h-full flex justify-center items-center">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="relative">
                            <Loader className="w-12 h-12 animate-spin text-blue-600"/>
                            <div
                                className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-blue-300 rounded-full animate-spin"></div>
                        </div>
                        <div className="text-center">
                            <h3 className="text-slate-700 font-semibold text-lg">Finding Expert Developer</h3>
                            <p className="text-slate-500 text-sm">AI-powered matching in progress...</p>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {!review && (
                        <div className="col-span-12 flex flex-col justify-center items-center">
                            <div className="flex gap-4 mt-0 w-full">
                                {/* Publish for Review Section - 1/3 width */}
                                <div className="w-1/3 flex flex-col gap-2">
                                    <div
                                        className="bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-4">
                                        <Button
                                            variant="twoTone"
                                            color="#3B82F6"
                                            loading={false}
                                            disabled={true}
                                            className="bg-blue-400 border border-blue-300 rounded-xl text-white font-semibold px-4 py-3 cursor-not-allowed opacity-80 flex items-center gap-2 w-full justify-center"
                                        >
                                            <Sparkles className="w-4 h-4"/>
                                            Publish for Review
                                        </Button>

                                        <div className="text-center mt-2">
                                            <p className="text-slate-600 text-xs mb-1">Publish to marketplace where
                                                developers can review this
                                                for you</p>
                                            <Badge size="sm"
                                                   classColor="bg-blue-100 text-blue-700 border border-blue-300">
                                                Coming Soon
                                            </Badge>
                                        </div>

                                        {/* Small checkbox under the main button */}
                                        <div className="bg-white/70 border border-slate-200 rounded-lg p-2 mt-2">
                                            <Checkbox color="blue-500" checked disabled>
                        <span className="text-slate-600 text-xs font-medium flex items-center gap-1">
                          Auto-assign to best developer
                        </span>
                                            </Checkbox>
                                        </div>
                                    </div>
                                </div>

                                {/* Thin vertical separator */}
                                <div className="w-px bg-slate-300 my-2"></div>

                                {/* Review PR Section - 2/3 width */}
                                <div className="w-2/3 flex flex-col gap-3">
                                    <div
                                        className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
                                        <Button
                                            variant="twoTone"
                                            color="#7C3AED"
                                            loading={shredReviewLoading}
                                            disabled={shredReviewLoading || !shredId}
                                            onClick={reviewFunc}
                                            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 border border-purple-400 rounded-xl text-white font-semibold px-4 py-3 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <GitPullRequest className="w-5 h-5"/>
                                            {shredReviewLoading ? 'Processing PR Comments...' : 'Review PR Comments'}
                                            <Sparkles className="w-4 h-4"/>
                                        </Button>

                                        <div className="mt-3 space-y-2">
                                            <div className="flex items-center gap-2 text-slate-700">
                                                <Eye className="w-4 h-4 text-purple-600"/>
                                                <span className="text-sm font-medium">ShredsAi can process your pull request comments and update the code accordingly.</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-600">
                                                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                                <span className="text-xs">Review the code in your repo and leave comments</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-600">
                                                <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                                                <span className="text-xs">ShredsAi processes your comments and updates the code</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <div
                                            className="bg-white/70 border border-slate-200 rounded-lg px-2 pt-2 flex-1">
                                            <Checkbox checked={compileAfterEnd} onChange={setCompileAfterEnd}
                                                      color="blue-500 mb-0">
                        <span className="text-slate-700 -ml-1 text-sm font-medium">
                          Compile & Test the Code After Updates
                        </span>
                                            </Checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {review && (
                        <>
                            <div className="col-span-4 flex items-center">
                                <div className="relative w-[90%] m-auto">
                                    <img
                                        className="rounded-2xl border-2 h-auto w-full border-slate-300 object-contain shadow-md"
                                        src="/images/user.jpg"
                                        width={100}
                                        height={100}
                                        alt="architect"
                                    />
                                    <div
                                        className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-8">
                                <div className="flex justify-end mb-2">
                                    <Button
                                        loading={false}
                                        className="flex items-center justify-between gap-2 font-semibold bg-white border border-slate-300 text-slate-700 rounded-lg px-3 py-1.5 shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <ReactCountryFlag
                                            className="rounded w-4 h-4"
                                            countryCode="us"
                                            svg
                                        />
                                        USA
                                    </Button>
                                </div>
                                <div>
                                    <h1 className="font-bold text-xl text-slate-800 flex items-center gap-2">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        Robert Fox
                                    </h1>
                                    <p className="text-slate-600 font-medium text-sm">
                                        Senior Java Developer
                                    </p>
                                    <div
                                        className="flex items-center justify-between w-full max-w-[180px] font-semibold mt-2 bg-white/50 rounded-lg p-2 border border-slate-200">
                                        <Calendar className="w-4 h-4 text-slate-500"/>
                                        <span className="text-sm font-medium text-slate-700">
                      Experience
                    </span>
                                        <Badge size="sm"
                                               classColor="bg-emerald-100 text-emerald-700 border border-emerald-300">
                                            14 years
                                        </Badge>
                                    </div>
                                    <Button
                                        loading={false}
                                        disabled={true}
                                        className="bg-orange-100 text-orange-700 border border-orange-300 rounded-xl font-medium flex items-center gap-2 mt-3 mb-3 cursor-not-allowed opacity-70 px-4 py-2 w-full"
                                    >
                                        <Loader className="w-4 h-4 animate-spin"/>
                                        Review in Progress
                                    </Button>
                                    <Button
                                        loading={false}
                                        className="bg-purple-500 hover:bg-purple-600 border border-purple-400 rounded-xl text-white font-medium flex items-center gap-2 mt-2 mb-7 px-4 py-2 transition-all duration-200 shadow-md hover:shadow-lg w-full"
                                    >
                                        <Store className="w-4 h-4"/>
                                        Visit Marketplace
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default PublishForReview;